import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { calculateStayDuration, formatCheckInDate } from "@Utils/helpers/helpers";
import { currencySymbols } from "@Static/static";
import Button from "@UI/kit/Button/Button";
import FormContainer from "@UI/kit/FormContainer/FormContainer";
import { ReactComponent as Card } from "@Icons/payments/Card.svg";
import { ReactComponent as ArrowRight } from "@Icons/payments/ArrowRight.svg";
import { useTranslation } from "react-i18next";
import { axiosPay, getPaymentProviders, getPaymentProvidersByParkingId } from "@API/axiosRequest";
import { useModal } from "@Contexts/ModalContext";
import Loader from "../../../../UI/kit/Loader/Loader";
import { paymentCheck } from "../../../../api/axiosRequest";
import { NetworkContext } from "../../../../contexts/NetworkContext";

function Payment() {
  const { isOnline } = useContext(NetworkContext);
  const location = useLocation();
  const { info, plateNumber, parkingUid, selectedProvider } = location.state;
  const initialLoad = useRef(true);
  const [checkResponse, setCheckResponse] = useState();
  const [paymentProvider, setPaymentProvider] = useState(selectedProvider || null);
  const [availablePaymentProviders, setAvailablePaymentProviders] = useState([]);
  const [paymentProviders, setPaymentProviders] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();
  const { openModal } = useModal();

  const check = useCallback(async () => {
    await paymentCheck(plateNumber, parkingUid).then((res) => {
      setCheckResponse(res.data);
    });
  }, []);

  const onChangePayment = (method) => {
    setPaymentProvider(method);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const trimmedPlateNumber = plateNumber?.trim();
    const successUrl = `${window.location.origin}/payment-success/${parkingUid}/${trimmedPlateNumber}`.trim();
    const errorUrl = `${window.location.origin}/payment-error`.trim();

    if (parkingUid === "112") {
      setIsLoading(true);
      await axiosPay("STRIPE", trimmedPlateNumber, parkingUid, successUrl, errorUrl)
        .then((res) => {
          window.location.href = res?.data?.url;
        })
        .finally(() => setIsLoading(false));
    } else if (paymentProvider === "KASPI") {
      window.location.href = availablePaymentProviders.find((method) => method.paymentType === "KASPI")?.link;
    } else if (paymentProvider === "WAFACASH") {
      openModal("phoneVerification", {
        plateNumber,
        parkingUid,
      });
    } else if (paymentProvider === "SAFARICOM") {
      openModal("phoneVerification", {
        plateNumber,
        parkingUid,
        provider: "mpesa"
      });
    } else {
      setIsLoading(true);
      await axiosPay(paymentProvider, trimmedPlateNumber, parkingUid, successUrl, errorUrl)
        .then((res) => {
          window.location.href = res?.data?.url;
        })
        .finally(() => setIsLoading(false));
    }
  };

  useEffect(() => {
    if (initialLoad.current) {
      setIsLoading(true);

      check()
        .then(() => {
          getPaymentProviders().then((providers) => {
            const methods = providers.data.reduce((acc, provider) => {
              acc[provider?.providerName] = {
                img: provider?.iconUrl,
                text: provider?.providerName,
              };
              return acc;
            }, {});

            getPaymentProvidersByParkingId(parkingUid).then((res) => {
              const availableMethods = res.data.map((method) => ({
                paymentType: method.paymentType,
                link: method.link,
                ...methods[method.paymentType],
              }));

              if (availableMethods.length === 1) {
                setPaymentProvider(availableMethods[0].paymentType);
              }
              setAvailablePaymentProviders(availableMethods);
              setPaymentProviders(methods);
            });
          });
        })
        .finally(() => setIsLoading(false));

      initialLoad.current = false;
    }
  }, []);

  if (!isOnline) {
    return (
      <FormContainer>
        <div className="flex w-full flex-col items-center space-y-3">
          <p className="text-center text-alertRed">{t("no_internet_connection")}</p>
        </div>
      </FormContainer>
    );
  }

  if (isLoading) {
    return (
      <FormContainer
        title={<p className="mb-4 text-center text-2xl text-textNormal">{t("parking_session_details")}</p>}
      >
        <Loader />
      </FormContainer>
    );
  }

  return (
    <FormContainer title={<p className="mb-4 text-center text-2xl text-textNormal">{t("parking_session_details")}</p>}>
      <div className="mb-4 flex w-full flex-col justify-center space-y-6">
        <div>
          <div className="infoCar_info border-none">
            <span>{t("current_balance")}</span>
            <span>
              {info?.currentBalance?.toLocaleString("en").replace(/,/g, " ") || info.current_balance}{" "}
              {currencySymbols[info?.currency?.toUpperCase()]}
            </span>
          </div>
          <div className="infoCar_info">
            <span>{t("plate_number")}</span>
            <span>{plateNumber}</span>
          </div>
          {info?.inDate && (
            <div className="infoCar_info">
              <span>{t("entrance_time")}</span>
              <span>{formatCheckInDate(info.inDate)}</span>
            </div>
          )}
          <div className="infoCar_info">
            <span>{t("parking_duration")}</span>
            <span>
              {info?.durationInMinutes
                ? info.in_date === ""
                  ? "No entries"
                  : Math.abs(info.durationInMinutes) < 60
                    ? Math.abs(info.durationInMinutes) + " minutes"
                    : Math.floor(Math.abs(info.durationInMinutes) / 60) +
                      ` ${info.durationInMinutes > 1 ? "hours" : "hour"} and ` +
                      (Math.abs(info.durationInMinutes) % 60) +
                      " minutes"
                : calculateStayDuration(info.inDateISO)}
            </span>
          </div>
          <div className="infoCar_info highlighted">
            <span className="highlighted">{t("total")}:</span>
            <span>
              {checkResponse?.sum} {currencySymbols[info?.currency?.toUpperCase()]}
            </span>
          </div>
        </div>

        <button
          className="mx-auto flex h-14 w-full max-w-[300px] items-center rounded-2xl border p-4"
          type="button"
          onClick={() => {
            availablePaymentProviders &&
              openModal("choosePayment", {
                onChange: onChangePayment,
                availablePaymentProviders,
              });
          }}
          disabled={availablePaymentProviders.length === 0}
        >
          {paymentProvider && (
            <div className="flex items-center space-x-3">
              <img src={paymentProviders[paymentProvider]?.img} alt="Payment Method" className="h-7" />
              <p>{paymentProviders[paymentProvider]?.text}</p>
            </div>
          )}

          {!paymentProvider && (
            <div className="flex items-center space-x-3">
              <Card />
              <p className="text-sm">{t("choose_payment_method")}</p>
              <ArrowRight />
            </div>
          )}
        </button>

        <div className="flex w-full flex-col items-center">
          <Button
            disabled={parkingUid !== "112" && !paymentProvider}
            type="submit"
            onClick={handleSubmit}
            text={isLoading ? t("loading") : t("pay")}
          />
        </div>
      </div>
    </FormContainer>
  );
}

export default Payment;
